/**
 * File: assets/basari-contact-lens-eyes.js
 * Purpose: The "Which eyes?" choice on the contact lens product page
 *          (Both eyes / Right eye only / Left eye only).
 *
 * The choice is a front for the per-eye checkboxes rendered by
 * snippets/basari-contact-lens-eye.liquid. Those checkboxes stay the single
 * source of truth: basari-contact-lens.js reads them to show or hide each eye's
 * column, validate, set the cart quantity and leave an excluded eye's values
 * out of the cart, and basari-api-integration.js reads them for the quote and
 * the per-eye cart lines. This file only sets them, the way a click would, and
 * mirrors them back onto the choice.
 *
 * - An eye that is switched off keeps its power and boxes, so switching back
 *   restores them.
 * - The other eye is always switched on before one is switched off, so the
 *   theme's "at least one eye" rule is never hit.
 * - One delegated listener per page: re-rendered sections (theme editor,
 *   section rendering) need no new binding and cannot get a second one.
 *
 * Load after basari-contact-lens.js:
 *   <script src="{{ 'basari-contact-lens-eyes.js' | asset_url }}" defer="defer"></script>
 */
(function () {
  'use strict';

  if (window.__basariEyeChoice) return;
  window.__basariEyeChoice = true;

  var EYES = ['right', 'left'];

  function powerOf(node) {
    return node && node.closest ? node.closest('[data-basari-cl-power]') : null;
  }

  function eyeBox(power, eye) {
    return power.querySelector('[data-basari-cl-power-eye-input][data-eye="' + eye + '"]');
  }

  function radios(power) {
    return Array.prototype.slice.call(power.querySelectorAll('[data-basari-cl-eye-choice-input]'));
  }

  // What the checkboxes say now: 'both', 'right' or 'left'.
  function currentChoice(power) {
    var right = eyeBox(power, 'right');
    var left = eyeBox(power, 'left');
    var r = !right || right.checked;
    var l = !left || left.checked;
    if (r && l) return 'both';
    return r ? 'right' : 'left';
  }

  // Same effect as the shopper ticking or unticking the box.
  function setEye(power, eye, on) {
    var box = eyeBox(power, eye);
    if (!box || box.checked === on) return;
    box.checked = on;
    box.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function reflect(power) {
    var choice = currentChoice(power);
    radios(power).forEach(function (radio) {
      radio.checked = radio.value === choice;
    });
    power.setAttribute('data-eye-choice', choice);
  }

  function apply(power, choice) {
    var want = { right: choice !== 'left', left: choice !== 'right' };
    // Switch on first, then off: there is never a moment with no eye.
    EYES.forEach(function (eye) {
      if (want[eye]) setEye(power, eye, true);
    });
    EYES.forEach(function (eye) {
      if (!want[eye]) setEye(power, eye, false);
    });
    reflect(power);
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches) return;
    if (target.matches('[data-basari-cl-eye-choice-input]')) {
      var power = powerOf(target);
      if (power && target.checked) apply(power, target.value);
    } else if (target.matches('[data-basari-cl-power-eye-input]')) {
      var owner = powerOf(target);
      if (owner) reflect(owner);
    }
  });

  // The choice follows the checkboxes on load, after a section re-render and
  // when the browser restores a page from its back/forward cache.
  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-basari-cl-power]');
    for (var i = 0; i < nodes.length; i += 1) {
      if (nodes[i].querySelector('[data-basari-cl-eye-choice]')) reflect(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  window.addEventListener('pageshow', function () {
    init(document);
  });

  // For basari-api-integration.js: a product sold for one eye only offers
  // just that eye.
  window.BasariEyeChoice = {
    restrict: function (power, eyes) {
      if (!power) return;
      var sold = { right: !eyes || eyes.right !== false, left: !eyes || eyes.left !== false };
      if (sold.right && sold.left) return;
      var only = sold.right ? 'right' : 'left';
      apply(power, only);
      radios(power).forEach(function (radio) {
        var allowed = radio.value === only;
        radio.disabled = !allowed;
        var option = radio.closest('.basari-cl-eye-choice__option');
        if (option) option.hidden = !allowed;
      });
    }
  };
})();
